import { useNavigate } from "react-router"
import React, { forwardRef, Suspense, useRef } from "react"
import { FormikProps } from "formik"
import { ClosingTimeType, CreateMobileAVResource, CreateMobileAVResourceForm } from "../../resourcesTypes"
import { useApiMutation } from "../../../../../../../core/api/useApiMutation"
import { editResource, getMobileAVResource, ServiceCenterResourcesQuery } from "../../resourcesApi"
import { enqueueSnackbar } from "notistack"
import {
    SERVICE_CENTER_ADMIN_ORGANIZATION_RESOURCE_ROUTE,
    SERVICE_CENTER_ADMIN_ORGANIZATION_ROUTE,
    SERVICE_CENTER_ADMIN_ROUTE,
} from "../../../../../../../constants/routes"
import { LocalizedStrict } from "../../../../../../localization/components/AppLocalized"
import { PageHeader } from "../../../../../../../shared/components/pageHeader/PageHeader"
import { Page } from "../../../../../../../shared/components/pageHeader/PageHeaderApi"
import { Button } from "../../../../../../office/admin/components/Button"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../../../shared/components/links/StandardBreadcrumbs"
import { useParams } from "react-router-dom"
import { useApiQuery } from "../../../../../../../core/api/useApiQuery"
import { SupportEntityQuery } from "../../../../../../support/admin/entities/supportEntityApi"
import { LoadingSpinner } from "../../../../../../../shared/components/loading/LoadingSpinner"
import { QueryErrorBoundary } from "../../../../../../../core/api/components/QueryErrorBoundary"
import { CreateUpdateMobileAVForm } from "../../components/mobileAV/CreateUpdateMobileAVForm"
import { DateTimeFactory, useDateTime } from "../../../../../../localization/useDateTime"
import { transformMobileAVFormToResource } from "./utils"

export function EditMobileAV() {
    const mobileAVFormRef = useRef<FormikProps<CreateMobileAVResourceForm>>(null)

    const handleSaveClick = () => {
        mobileAVFormRef.current?.submitForm()
    }

    return (
        <>
            <LocalizedStrict
                id="service-center-admin-organization-resources-update-mobile-av-title"
                attrs={{ title: true }}
            >
                <PageHeader
                    page={Page.SERVICE_CENTER_ADMIN_ORGANIZATION_RESOURCE_CREATE_MOBILE_AV_ROUTE}
                    title="Edit Mobile AV"
                    rightElement={
                        <LocalizedStrict id="support-create-company-update-button">
                            <Button variant="contained" onClick={handleSaveClick}>
                                Update
                            </Button>
                        </LocalizedStrict>
                    }
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="merchant-nav-item-home">
                            <BreadcrumbLink to={SERVICE_CENTER_ADMIN_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="service-center-admin-nav-item-organization">
                            <BreadcrumbLink to={SERVICE_CENTER_ADMIN_ORGANIZATION_ROUTE}>Organization</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="service-center-admin-nav-item-organization-resource-update-mobile-av">
                            <Breadcrumb>Update Mobile AV</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>
            <QueryErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                    <MobileAV ref={mobileAVFormRef} />
                </Suspense>
            </QueryErrorBoundary>
        </>
    )
}

const transformData = (data: CreateMobileAVResource, dateTimeFactory: DateTimeFactory): CreateMobileAVResourceForm => {
    const { bookingTime, closingTimes, ...rest } = data
    const closingTime = closingTimes.filter((i) => i.type === ClosingTimeType.DAILY)?.[0]?.periodInTime

    return {
        ...rest,
        closingTimes,
        isClosingTimes: !!closingTime,
        isBookingTime: !!bookingTime,
        ...(bookingTime && {
            bookingTime: {
                periodInTime: {
                    from: dateTimeFactory.fromHourMinute(bookingTime?.periodInTime.from),
                    to: dateTimeFactory.fromHourMinute(bookingTime?.periodInTime?.to),
                },
            },
        }),
        ...(closingTime && {
            closingTimesDaily: {
                periodInTime: {
                    from: dateTimeFactory.fromHourMinute(closingTime.from),
                    to: dateTimeFactory.fromHourMinute(closingTime.to),
                },
            },
        }),
    }
}

const MobileAV = forwardRef<FormikProps<CreateMobileAVResourceForm>>((_props, ref) => {
    const navigate = useNavigate()
    const { mobileAvId } = useParams()
    const mobileAvNumber = Number(mobileAvId)
    const dateTimeFactory = useDateTime()

    const mobileAVDetails = useApiQuery(getMobileAVResource, {
        queryName: SupportEntityQuery.DETAILS,
        dependencies: {
            mobileAVId: mobileAvNumber ?? 0,
        },
        suspense: true,
        useErrorBoundary: true,
        enabled: Boolean(mobileAvNumber),
        select: (data) => transformData(data, dateTimeFactory),
    })

    const { mutate } = useApiMutation(editResource, {
        async onSuccess(_, data, context) {
            navigate(SERVICE_CENTER_ADMIN_ORGANIZATION_RESOURCE_ROUTE)
            await context.queryClient.invalidateQueries({
                queryKey: [ServiceCenterResourcesQuery.SERVICE_CENTER_RESOURCES],
            })
            if (data?.serviceResponse?.displayMessage) {
                enqueueSnackbar({
                    variant: "success",
                    message: data?.serviceResponse?.displayMessage,
                })
            }
        },
    })

    const editMobileAV = (value: CreateMobileAVResourceForm) => {
        if (!mobileAvNumber) throw new Error("Mobile AV ID is undefined")

        const updatedForm = transformMobileAVFormToResource(value, dateTimeFactory)

        mutate({
            roomId: mobileAvNumber,
            ...updatedForm,
        })
    }

    return <CreateUpdateMobileAVForm initialValues={mobileAVDetails.data} ref={ref} onSubmit={editMobileAV} />
})
